import * as custom from './marketplace-custom-config.js';
import { defaultMCC, stripePublishableKey, stripeCountryDetails } from './stripe-config';
import { currencyConfiguration } from './currency-config';

const env = process.env.REACT_APP_ENV;
const dev = process.env.REACT_APP_ENV === 'development';

// The language of a page comes from its URL (src/util/locale.js), not from config.
const i18n = {
  /*
    0: Sunday
    1: Monday
    ...
    6: Saturday
  */
  firstDayOfWeek: 0,
};

// Should search results be ordered by distance to origin.
// NOTE: If this is set to true, every suggested location needs an 'origin'.
// Without the 'origin' parameter, search will not work correctly
// NOTE: Keyword search and ordering search results by distance can't be used at the same time. You can turn keyword
// search off by removing keyword filter config from filters array in marketplace-custom-config.js
const sortSearchByDistance = false;

// API supports custom processes to be used in booking process.
// We need to specify it when we are initiating a new order
// (or fetching price information by calling 'initiateSpeculative' endpoint).
//
// In a way, 'processAlias' defines which transaction process (or processes)
// this particular web application is able to handle.
const bookingProcessAlias = 'flex-booking-process/release-1';
const paymentProcessAlias = 'flex-payment-process/release-1';

// The transaction line item code for the main unit type in bookings.
//
// Possible values: ['line-item/night', 'line-item/day', 'line-item/units';]
//
// Note 1: This 'bookingUnitType' variable affects only web app.
//         If you are using privileged transitions (which is used by the default process),
//         you also need to configure unit type in API server: server/api-util/lineItems.js
//
// Note 2: Translations will use different translation keys for night, day or unit
//         depending on the value chosen.
const bookingUnitType = 'line-item/units';

// Should the application fetch available time slots (currently defined as
// start and end dates) to be shown on listing page.
const enableAvailability = process.env.REACT_APP_AVAILABILITY_ENABLED === 'true';

// A maximum number of days forwards during which a booking can be made.
// This is limited due to Stripe holding funds up to 90 days from the
// moment they are charged:
// https://stripe.com/docs/connect/account-balances#holding-funds
//
// See also the API reference for querying time slots:
// https://www.sharetribe.com/api-reference/marketplace.html#query-time-slots
const dayCountAvailableForBooking = 365;

// To pass environment variables to the client app in the build
// script, the build (config/env.js) only passes variables with the REACT_APP_ prefix,
// so server secrets never reach the client side.
const sdkClientId = process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID;
const sdkBaseUrl = process.env.REACT_APP_SHARETRIBE_SDK_BASE_URL;
const sdkAssetCdnBaseUrl = process.env.REACT_APP_SHARETRIBE_SDK_ASSET_CDN_BASE_URL;
const sdkTransitVerbose = process.env.REACT_APP_SHARETRIBE_SDK_TRANSIT_VERBOSE === 'true';

// Marketplace currency.
// It should match one of the currencies listed in currency-config.js
const currencyConf = process.env.REACT_APP_SHARETRIBE_MARKETPLACE_CURRENCY;
const currency = currencyConf ? currencyConf.toUpperCase() : currencyConf;

// Currency formatting options.
// See: https://github.com/yahoo/react-intl/wiki/API#formatnumber
const currencyConfig = currencyConfiguration(currency);

// Listing minimum price in currency sub units, e.g. cents.
// 0 means no restriction to the price
const listingMinimumPriceSubUnits = 0;

// Sentry DSN (Data Source Name), a client key for authenticating calls to Sentry
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
// Staging also builds with REACT_APP_ENV=production, so Sentry gets its own name.
const sentryEnvironment = process.env.REACT_APP_SENTRY_ENVIRONMENT || env;

// If webapp is using SSL (i.e. it's behind 'https' protocol)
const usingSSL = process.env.REACT_APP_SHARETRIBE_USING_SSL === 'true';

// Akuntsu's address, as in the legal pages (C54), for the Organization in the SEO schema
// (http://schema.org/PostalAddress).
const address = {
  '@type': 'PostalAddress',
  streetAddress: '4060 boulevard Saint-Laurent, Suite 703',
  addressLocality: 'Montréal',
  addressRegion: 'QC',
  addressCountry: 'CA',
};

// Canonical root url is needed in social media sharing and SEO optimization purposes.
const canonicalRootURL = process.env.REACT_APP_CANONICAL_ROOT_URL;

// Site title is needed in meta tags (bots and social media sharing reads those)
const siteTitle = 'Attractr';

// Twitter handle is needed in meta tags (twitter:site). Start it with '@' character
const siteTwitterHandle = '@attractr';

// Instagram page is used in SEO schema (http://schema.org/Organization)
const siteInstagramPage = 'https://www.instagram.com/attractr';

// Facebook page is used in SEO schema (http://schema.org/Organization)
const siteFacebookPage = 'https://www.facebook.com/attractr/';

// Social logins & SSO

// Note: Facebook app id is also used for tracking:
// Facebook counts shares with app or page associated by this id
// Currently it is unset, but you can read more about fb:app_id from
// https://developers.facebook.com/docs/sharing/webmasters#basic
// You should create one to track social sharing in Facebook
const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID;

const maps = {
  mapboxAccessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,
  googleMapsAPIKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,

  search: {
    // Off (plan 6p, the DPO's conditions): place search only, the browser's location is never
    // asked for. On, the first suggestion in an empty place field would be "Current location".
    suggestCurrentLocation: false,

    // Distance in meters for calculating the bounding box around the
    // current location.
    currentLocationBoundsDistance: 1000,

    // Places suggested before the user types (the template's US cities are gone).
    defaults: [],

    // Limit location autocomplete to a one or more countries
    // using ISO 3166 alpha 2 country codes separated by commas.
    // If you want to limit the autocomplete, uncomment this value:
    // countryLimit: ['AU'],
  },

  // When fuzzy locations are enabled, coordinates on maps are
  // obfuscated randomly around the actual location.
  //
  // NOTE: This only hides the locations in the UI level, the actual
  // coordinates are still accessible in the HTTP requests and the
  // Redux store.
  fuzzy: {
    enabled: false,

    // Amount of maximum offset in meters that is applied to obfuscate
    // the original coordinates. The actual value is random, but the
    // obfuscated coordinates are withing a circle that has the same
    // radius as the offset.
    offset: 500,

    // Default zoom level when showing a single circle on a Map. Should
    // be small enough so the whole circle fits in.
    defaultZoomLevel: 13,

    // Color of the circle on the Map component.
    circleColor: '#c0392b',
  },
};

// Phone number input settings used
// to collect users phone number
const phoneNumberConfig = {
  // Attractr is based in Montréal and prices in CAD (B4).
  defaultCountry: 'ca',
  // The top of the field's country list, in this order (ISO codes, lowercase)
  preferredCountries: ['ca', 'us', 'fr'],
};

// Main search used in Topbar.
// This can be either 'keywords' or 'location'.
const mainSearchType = 'keywords';

// Hard-corded maximum video length that is currently
// used in EditListingPage & EditLiveRoomPage which
// doesn't allow uploading videos longer then as it
// is specified.
const videoSettings = {
  maxDuration: 120, // Max video duration in seconds
};

const userRoles = {
  audience: 'audience',
  creator: 'creator',
  organization: 'organization',
};

// We split listings into types (e.g categories) which are used to
// determine if the listing is a Production Page ('page' — was 'show'
// until the Aug 2026 rename; data migrated), a Live Room or a Community
// space. Read/compare via src/util/listingType.js.
const listingTypes = {
  page: 'page',
  'live-room': 'live-room',
  community: 'community',
};

const paymentTypes = {
  membership: 'membership',
  donation: 'donation',
};

// Possible options are no-listings and with-listings.
const landingPageLayout = 'with-listings';

const userTiers = {
  enthusiast: {
    id: 'enthusiast',
    min: 100,
    max: 1000,
    avatarClassName: 'enthusiastTierWrapper',
  },
  connoisseur: {
    id: 'connoisseur',
    min: 1001,
    max: 5000,
    avatarClassName: 'connoisseurTierWrapper',
  },
  insider: {
    id: 'insider',
    min: 5001,
    max: 15000,
    avatarClassName: 'insiderTierWrapper',
  },
  trailblazer: {
    id: 'trailblazer',
    min: 15001,
    max: 50000,
    avatarClassName: 'trailblazerTierWrapper',
  },
  muse: {
    id: 'muse',
    min: 50001,
    max: Infinity,
    avatarClassName: 'museTierWrapper',
  },
};

// NOTE: only expose configuration that should be visible in the
// client side, don't add any server secrets in this file.
const config = {
  env,
  dev,
  bookingProcessAlias,
  paymentProcessAlias,
  bookingUnitType,
  enableAvailability,
  dayCountAvailableForBooking,
  i18n,
  sdk: {
    clientId: sdkClientId,
    baseUrl: sdkBaseUrl,
    assetCdnBaseUrl: sdkAssetCdnBaseUrl,
    transitVerbose: sdkTransitVerbose,
  },
  sortSearchByDistance,
  currency,
  currencyConfig,
  listingMinimumPriceSubUnits,
  stripe: {
    defaultMCC: defaultMCC,
    publishableKey: stripePublishableKey,
    supportedCountries: stripeCountryDetails,
  },
  canonicalRootURL,
  address,
  siteTitle,
  siteFacebookPage,
  siteInstagramPage,
  siteTwitterHandle,
  facebookAppId,
  sentryDsn,
  sentryEnvironment,
  usingSSL,
  maps,
  custom,
  phoneNumberConfig,
  mainSearchType,
  videoSettings,
  userRoles,
  listingTypes,
  paymentTypes,
  landingPageLayout,
  userTiers,
};

export default config;
